/**
 * PayTuition · standalone tuition-payment page for families who need to pay AFTER
 * their orientation submission. Bookmarkable, email-gated, isolated Q5 UI.
 *   /pay-tuition                  → email gate
 *   /pay-tuition?email=xxx        → jumps straight to family cards
 *
 * Behavior:
 *   - Enter parent email → look up family via /academy/orientation-portal/lookup
 *   - Render each student's plan cards
 *   - Choose plan → hit /tuition endpoint → show green Pay Now button
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { HandCoins, ExternalLink, Mail, Loader2, ShieldCheck } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const api = axios.create({ baseURL: `${API_BASE}/api` });

const PayTuition = () => {
  const [params, setParams] = useSearchParams();
  const emailParam = params.get('email') || '';
  const [email, setEmail] = useState(emailParam);
  const [loading, setLoading] = useState(false);
  const [familyTuition, setFamilyTuition] = useState(null);
  const [parentInfo, setParentInfo] = useState(null);
  const [selections, setSelections] = useState({});
  const [results, setResults] = useState({});

  useEffect(() => {
    if (emailParam) lookup(emailParam);
  }, []); // eslint-disable-line

  const lookup = async (e) => {
    if (!e) return;
    setLoading(true);
    try {
      const res = await api.post('/academy/orientation-portal/lookup', { email: e });
      const ft = res.data?.family_tuition;
      if (!ft) {
        toast.error('No tuition plans on file for this email. Please check with Margaret.');
        setFamilyTuition(null);
      } else {
        setFamilyTuition(ft);
        setParentInfo({
          first: res.data?.prefill?.parent_first_name || '',
          last:  res.data?.prefill?.parent_last_name || '',
          email: e,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  const submitEmail = (ev) => {
    ev?.preventDefault();
    if (!email) return;
    setParams({ email });
    lookup(email);
  };

  const confirmPlan = async (studentName, studentKey, planKey) => {
    if (!planKey) { toast.error('Choose a plan first'); return; }
    try {
      const res = await api.post('/academy/orientation-portal/tuition', {
        student_name: studentName,
        parent_email: email,
        plan: planKey,
        billing_contact_email: email,
      });
      setResults((prev) => ({ ...prev, [studentKey]: res.data }));
      toast.success(`${studentName} · plan confirmed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record plan');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="border-b border-zinc-800 pb-6 mb-8">
          <div className="text-[10px] font-black tracking-[0.3em] uppercase text-amber-400">Scholar Athlete Academy</div>
          <h1 className="text-3xl md:text-4xl font-black mt-2">Complete Your Tuition Payment</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Isolated payment page for families who already completed orientation. Enter the parent email you used on your orientation packet — we&rsquo;ll surface your specific family&rsquo;s plans.
          </p>
        </div>

        {!familyTuition ? (
          <form onSubmit={submitEmail} className="max-w-md">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-2">Parent Email</div>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500"
                data-testid="pay-tuition-email"
              />
              <button
                type="submit"
                disabled={loading || !email}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[11px] font-black tracking-widest uppercase px-4 py-2.5"
                data-testid="pay-tuition-lookup">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Look Up
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              This page is for parents who&rsquo;ve already submitted their orientation packet and need to complete tuition payment. If you haven&rsquo;t completed orientation yet, please start at <a href="/orientation" className="text-amber-400 underline">/orientation</a>.
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <div className="border border-zinc-800 p-4 bg-zinc-950">
              <div className="text-[10px] font-black tracking-widest uppercase text-amber-400">Family on File</div>
              <div className="text-xl font-black mt-1">{familyTuition.family_label}</div>
              {parentInfo?.first && (
                <div className="text-xs text-zinc-400 mt-1">{parentInfo.first} {parentInfo.last} · {parentInfo.email}</div>
              )}
            </div>

            {familyTuition.students.map((stu) => {
              const isOneTime = stu.plan_type === 'one_time';
              const nPlans = Object.keys(stu.plans).length;
              const gridCls = isOneTime ? 'md:grid-cols-1' : nPlans >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';
              const selected = selections[stu.student_key];
              const done = results[stu.student_key];
              return (
                <div key={stu.student_key} className="border border-zinc-800 p-5 bg-zinc-950">
                  <div className="text-[10px] font-black tracking-widest uppercase text-zinc-400">Student</div>
                  <div className="text-xl font-black mb-4">{stu.student_name} · {isOneTime ? 'Final Payment' : 'Choose One Plan'}</div>

                  <div className={`grid ${gridCls} gap-3`}>
                    {Object.entries(stu.plans).map(([pk, meta]) => {
                      const active = selected === pk;
                      const hasLink = Boolean(meta.payment_link);
                      const cadence = isOneTime ? 'one-time'
                                    : pk === 'monthly'   ? 'per month'
                                    : pk === 'quarterly' ? 'per quarter'
                                    : pk === 'biannual'  ? 'per 6 months' : 'one-time';
                      return (
                        <button
                          key={pk}
                          type="button"
                          onClick={() => setSelections((prev) => ({ ...prev, [stu.student_key]: pk }))}
                          className={`text-left border-2 p-4 transition-colors ${active ? 'border-emerald-400 bg-emerald-500/10' : 'border-zinc-800'}`}
                          data-testid={`pay-${stu.student_key}-${pk}`}>
                          <div className="text-xs font-black text-white">{meta.label}</div>
                          <div className="text-2xl font-black text-white mt-1">${meta.rate.toFixed(2)}</div>
                          <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mt-0.5">{cadence}</div>
                          <div className="text-[10px] text-zinc-400 mt-2 leading-snug">{meta.schedule}</div>
                          <div className={`text-[9px] font-black tracking-widest uppercase mt-2 ${hasLink ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {hasLink ? '✓ Instant Stripe Checkout' : '⏳ Invoice Sent Manually'}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {done ? (
                    (() => {
                      const chosenPlan = stu.plans[done.record.plan];
                      const link = chosenPlan?.payment_link;
                      const payUrl = link ? `${link}${link.includes('?') ? '&' : '?'}prefilled_email=${encodeURIComponent(email)}` : null;
                      return (
                        <div className="mt-4 border border-emerald-800 bg-emerald-950/50 p-4">
                          <div className="text-[10px] font-black tracking-widest text-emerald-400 uppercase">Plan Confirmed</div>
                          <div className="mt-1 text-sm text-white">{done.record.plan.toUpperCase()} · ${done.record.rate.toFixed(2)}</div>
                          {payUrl ? (
                            <a href={payUrl} target="_blank" rel="noreferrer"
                               className="mt-3 inline-flex items-center gap-2 text-xs font-black tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 px-4 py-2.5 uppercase"
                               data-testid={`pay-now-${stu.student_key}`}>
                              <HandCoins className="w-4 h-4" /> Pay Now · ${done.record.rate.toFixed(2)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <div className="mt-3 text-[10px] text-amber-400 border border-amber-500/40 bg-amber-500/5 p-2">
                              Stripe invoice generation is queued for Margaret. Watch your inbox.
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => confirmPlan(stu.student_name, stu.student_key, selected)}
                        disabled={!selected}
                        className="inline-flex items-center gap-2 text-xs font-black tracking-widest bg-emerald-500 text-black px-4 py-2.5 hover:bg-emerald-400 disabled:opacity-50 uppercase"
                        data-testid={`confirm-${stu.student_key}`}>
                        <HandCoins className="w-4 h-4" /> Confirm {stu.student_name.split(' ')[0]}&rsquo;s Plan
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="border border-zinc-800 bg-zinc-950 p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-zinc-400 leading-relaxed">
                Payments are processed by Stripe. Your card information never touches SAA servers. If any plan shows the amber &ldquo;Invoice Sent Manually&rdquo; label, Margaret will email you a direct Stripe invoice within 24 hours.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayTuition;
